import { useEffect, useLayoutEffect, useState } from "react";
import { useInView } from "react-intersection-observer";
import { SCROLL_CONTAINER_ID } from "components/Layouts/AutoFitContentInScreen";

// Drives infinite paging via an IntersectionObserver sentinel row. Resolves the
// scroll container synchronously before paint (via useLayoutEffect) and skips
// the observer until the container is in the DOM — otherwise the first render
// would briefly observe against the viewport with the wrong root, which on a
// short viewport could trigger an unwanted early fetch.
export default function useInfiniteScrollSentinel({
	hasNextPage,
	isFetchingNextPage,
	fetchNextPage,
	isError = false,
	rootMargin = "100px 0px",
	containerId = SCROLL_CONTAINER_ID,
	useViewportRoot = false,
}) {
	const [scrollRoot, setScrollRoot] = useState(null);
	useLayoutEffect(() => {
		if (useViewportRoot) return;
		setScrollRoot(document.getElementById(containerId));
	}, [containerId, useViewportRoot]);

	const rootReady = useViewportRoot || scrollRoot !== null;

	const { ref: sentinelRef, inView } = useInView({
		root: useViewportRoot ? null : scrollRoot,
		rootMargin,
		threshold: 0,
		skip: !rootReady,
	});

	// Skip when the previous next-page fetch errored. Otherwise the effect would
	// re-fire as soon as isFetchingNextPage flips back to false (with inView and
	// hasNextPage still true), looping requests on persistent failures.
	useEffect(() => {
		if (isError) return;
		if (inView && hasNextPage && !isFetchingNextPage && fetchNextPage) {
			fetchNextPage();
		}
	}, [inView, hasNextPage, isFetchingNextPage, fetchNextPage, isError]);

	return { sentinelRef };
}

export function scrollTableToTop(containerId = SCROLL_CONTAINER_ID) {
	const el = document.getElementById(containerId);
	if (el) {
		el.scroll({ top: 0, left: 0, behavior: "smooth" });
	} else {
		window.scroll({ top: 0, left: 0, behavior: "smooth" });
	}
}
